import { lazy, Suspense, useEffect, useState } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { CurrentUserProvider, useCurrentUser } from './CurrentUserContext.jsx';
import { SiteAccessProvider, useSiteAccess, isSiteAccessBlocked } from './SiteAccessContext.jsx';
import { SandboxPassContext } from './SandboxPassContext.jsx';
import { readPassFromLocation, verifySandboxPass } from '../features/auth/sandboxPass.js';
import { api } from '../lib/apiClient.js';
import { AppShell } from './AppShell.jsx';
import { RequireRoute } from './RouteGuards.jsx';
import { renderLegacyRedirectRoutes } from './legacyRedirects.jsx';
import { resolveLanding } from '../lib/onboardingState.js';
import { useClientConfig } from '../lib/clientConfig.js';
import { rbac } from '@dha-nexus/shared';

import { PersonaLoginPage } from '../features/auth/PersonaLoginPage.jsx';
import { ChangePasswordPage } from '../features/auth/ChangePasswordPage.jsx';
import { NexusHomePage } from '../features/home/NexusHomePage.jsx';
import { ExecutiveCommandCenterPage } from '../features/executive/ExecutiveCommandCenterPage.jsx';
import { PortfolioListPage } from '../features/portfolios/PortfolioListPage.jsx';
import { PortfolioProfilePage } from '../features/portfolios/PortfolioProfilePage.jsx';
import { ProgramListPage, ProgramProfilePage } from '../features/programs/ProgramPages.jsx';
import { ProjectListPage, ProjectProfilePage } from '../features/projects/ProjectPages.jsx';
import { RapidCommandCenterPage, RapidDetailPage } from '../features/rapid/RapidPages.jsx';
import { ProjectGateReviewPage } from '../features/gateReview/ProjectGateReviewPage.jsx';
import { IntegratedDeliveryWorkspacePage } from '../features/delivery/IntegratedDeliveryWorkspacePage.jsx';
import { WorkItemDetailPage } from '../features/delivery/WorkItemDetailPage.jsx';
import { StrategicRoadmapPage } from '../features/roadmap/StrategicRoadmapPage.jsx';
import {
  FinancialAdjustmentsPage,
  FinancialLifecycleManagerPage,
  FundingSourcesPage,
} from '../features/financials/FinancialLifecycleManagerPage.jsx';
import { PortfolioLifecycleManagementPage } from '../features/lifecycle/PortfolioLifecycleManagementPage.jsx';
import { StrategicDecisionWorkspacePage } from '../features/decision/StrategicDecisionWorkspacePage.jsx';
import { MarketResearchTrackerPage } from '../features/marketResearch/MarketResearchTrackerPage.jsx';
import { AiOrchestrationHubPage } from '../features/ai/AiOrchestrationHubPage.jsx';
import { AiRunDetailPage } from '../features/ai/AiRunDetailPage.jsx';
import { AiAskPage } from '../features/ai/AiAskPage.jsx';
import { DataIntegrationRbacSettingsPage } from '../features/admin/DataIntegrationRbacSettingsPage.jsx';
import { AdminConfigurationPage } from '../features/admin/AdminConfigurationPages.jsx';
import { ComplianceReadinessPage } from '../features/admin/ComplianceReadinessPage.jsx';
import {
  IngestionCenterPage,
  IngestionJobDetailPage,
} from '../features/admin/IngestionCenterPages.jsx';
import { ConnectorDetailPage } from '../features/admin/ConnectorDetailPage.jsx';
import { LineageExplorerPage } from '../features/admin/LineageExplorerPage.jsx';
import { RbacAdminPage } from '../features/admin/RbacAdminPage.jsx';
import { AccessRequestQueuePage } from '../features/admin/AccessRequestQueuePage.jsx';
import { AccessReviewDetailPage } from '../features/admin/AccessReviewDetailPage.jsx';
import { SiteAccessAdminPage } from '../features/admin/SiteAccessAdminPage.jsx';
import { UserAdminPage } from '../features/admin/UserAdminPage.jsx';
import { AuthenticationAdminPage } from '../features/admin/AuthenticationAdminPage.jsx';
import { AuditLogPage } from '../features/admin/AuditLogPage.jsx';
import { ActivityReportsPage } from '../features/admin/ActivityReportsPage.jsx';
import { GuideResultsWorkspacePage } from '../features/admin/GuideResultsWorkspacePage.jsx';
import { ActivityReportViewerPage } from '../features/admin/ActivityReportViewerPage.jsx';
import { AccessRequestPage } from '../features/access/AccessRequestPage.jsx';
import { AccessStatusPage } from '../features/access/AccessStatusPage.jsx';
import { PublicRequestAccessPage } from '../features/access/PublicRequestAccessPage.jsx';
import { SiteAccessPortalPage } from '../features/access/SiteAccessPortalPage.jsx';
import { SearchResultsPage } from '../features/search/SearchResultsPage.jsx';
import { MyFeedbackPage } from '../features/feedbackMarkup/MyFeedbackPage.jsx';
import { FeedbackSubmittedPage } from '../features/feedbackMarkup/FeedbackSubmittedPage.jsx';
import { FeedbackWorkspacePage } from '../features/feedbackMarkup/FeedbackWorkspacePage.jsx';
import { FeedbackQueuePage } from '../features/feedback/FeedbackQueuePage.jsx';
import { SupportTicketPage } from '../features/feedbackIntake/SupportTicketPage.jsx';
import { RequestIntakePage } from '../features/feedbackIntake/RequestIntakePage.jsx';
import { HelpQuestionsPage } from '../features/helpQuestions/HelpQuestionsPage.jsx';
import { NotificationsPage } from '../features/me/NotificationsPage.jsx';
import { ActivityPage } from '../features/me/ActivityPage.jsx';
import { SavedViewsPage } from '../features/me/SavedViewsPage.jsx';
import { BriefingDetailPage } from '../features/briefings/BriefingDetailPage.jsx';
import { BriefingProductFactoryPage } from '../features/briefings/BriefingProductFactoryPage.jsx';
import {
  PublicStatusPage,
  StatusPackDetailPage,
  StatusPackLibraryPage,
} from '../features/status/StatusPackPages.jsx';
import { MyProfilePage } from '../features/me/MyProfilePage.jsx';
import { UserSettingsPage } from '../features/me/UserSettingsPage.jsx';
import { AccessDeniedPage } from '../features/errors/AccessDeniedPage.jsx';
import { NotFoundPage } from '../features/errors/NotFoundPage.jsx';
import { ActionCenterPage, ActionDetailPage } from '../features/actions/ActionCenterPages.jsx';
import { MeetingDetailPage, MeetingManagerPage } from '../features/actions/MeetingManagerPages.jsx';
import {
  ArtifactDetailPage,
  CloseoutPackagePage,
  KnowledgeLibraryPage,
  LessonsLearnedLibraryPage,
} from '../features/knowledge/KnowledgePages.jsx';
import {
  LoeDetailPage,
  LoeDashboardPage,
  ProjectFactoryCreatePage,
  ProjectFactoryPage,
  ProjectFactoryRequestPage,
} from '../features/owha/ProjectFactoryPages.jsx';
import {
  AssignmentWizardPage,
  ResourceConflictDetailPage,
  ResourceCommandCenterPage,
  ResourceSkillsMatrixPage,
} from '../features/resources/ResourcePages.jsx';
import {
  CurrentTimesheetPage,
  TimesheetDetailPage,
  TimesheetHomePage,
} from '../features/time/TimesheetPages.jsx';
import {
  ChangeRequestsPage,
  EvmDashboardPage,
  GovernanceHomePage,
  IssueLogPage,
  RiskRegisterPage,
} from '../features/governance/GovernancePages.jsx';
import { UatDashboardPage } from '../features/uat/UatDashboardPage.jsx';

const STATUS_SHARE_ROUTE_PATTERN = /^\/status\/[^/]+$/;

function RootRedirect() {
  const { user, defaultLanding, loading, auth } = useCurrentUser();
  if (loading) return <div className="empty-state">Loading…</div>;
  if (!user) return <Navigate to="/login" replace />;
  if (auth?.mustChangePassword) return <Navigate to="/change-password" replace />;
  // First-run intercept (WP3): route an SME who has not dismissed the welcome
  // to /home so it can greet them; everyone else keeps their default landing.
  return <Navigate to={resolveLanding(defaultLanding || '/home', user.id)} replace />;
}

function guardedElement(permissionPath, element) {
  return (
    <RequireRoute roles={rbac.ROUTE_PERMISSIONS[permissionPath] || []}>{element}</RequireRoute>
  );
}

// Help & getting-started center (WP6). These are the first code-split routes in
// the app: each page is React.lazy-loaded behind a lightweight Suspense fallback
// that matches the shell's existing loading idiom.
const HelpCenterPage = lazy(() => import('../features/help/HelpCenterPage.jsx'));
const GlossaryPage = lazy(() => import('../features/help/GlossaryPage.jsx'));
const CapabilityMapPage = lazy(() => import('../features/help/CapabilityMapPage.jsx'));

function guardedLazy(permissionPath, LazyComponent) {
  return guardedElement(
    permissionPath,
    <Suspense fallback={<div className="empty-state">Loading…</div>}>
      <LazyComponent />
    </Suspense>,
  );
}

function SiteAccessRouteRedirect() {
  const clientConfig = useClientConfig();
  const location = useLocation();
  const { loading, user } = useCurrentUser();
  const sandboxFrontDoorEnabled =
    clientConfig?.sandbox?.frontDoorEnabled === true ||
    clientConfig?.flags?.sandboxFrontDoorEnabled === true;
  const adminOrigin = clientConfig?.sandbox?.adminOrigin || null;
  const onDedicatedAdminHost =
    typeof window !== 'undefined' &&
    adminOrigin &&
    String(window.location.origin || '').replace(/\/+$/, '') ===
      String(adminOrigin).replace(/\/+$/, '');
  if (sandboxFrontDoorEnabled && !onDedicatedAdminHost) {
    return <Navigate to={{ pathname: '/request-access', search: location.search }} replace />;
  }
  if (loading) return <div className="empty-state">Loading…</div>;
  return <Navigate to={user ? '/home' : '/login'} replace />;
}

function effectiveAuthMode(clientConfig) {
  return (
    clientConfig?.auth?.effectiveMode || clientConfig?.flags?.authMode || 'password_first_hybrid'
  );
}

function sandboxMode(clientConfig) {
  return clientConfig?.sandbox?.mode || clientConfig?.flags?.sandboxMode || 'disabled';
}

function passwordCapableLoginSurface(clientConfig) {
  const currentSandboxMode = sandboxMode(clientConfig);
  const sandboxFrontDoorEnabled =
    clientConfig?.sandbox?.frontDoorEnabled === true ||
    clientConfig?.flags?.sandboxFrontDoorEnabled === true;
  return (
    sandboxFrontDoorEnabled ||
    currentSandboxMode === 'sandbox' ||
    effectiveAuthMode(clientConfig) === 'password_first_hybrid'
  );
}

function normalizedOrigin(value) {
  return String(value || '')
    .trim()
    .replace(/\/+$/, '');
}

// WP1 — sandbox routing-pass entry guard.
//
// Placement: this wraps the whole route tree INSIDE CurrentUserProvider (and
// therefore inside the non-blocked branch of SiteAccessBoundary). It needs the
// current-user bootstrap (authenticated sessions bypass the pass entirely) and
// wrapping AppRoutes covers bare `/`, `/login`, and every deep link on gated
// hosts with a single component.
//
// Gated hosts are: private sandbox instances (sandbox.mode === 'sandbox') and
// the dedicated admin host (front_door mode served from sandbox.adminOrigin).
// Everywhere else — local dev, disabled mode, the public front door — the
// guard renders children untouched.
function SandboxEntryGuard({ children }) {
  const clientConfig = useClientConfig();
  const { loading: siteAccessLoading } = useSiteAccess();
  const { user, loading: userLoading } = useCurrentUser();
  const [verification, setVerification] = useState(null);

  const currentSandboxMode = sandboxMode(clientConfig);
  const adminOrigin = clientConfig?.sandbox?.adminOrigin || null;
  const onDedicatedAdminHost =
    currentSandboxMode === 'front_door' &&
    typeof window !== 'undefined' &&
    Boolean(adminOrigin) &&
    normalizedOrigin(window.location.origin) === normalizedOrigin(adminOrigin);
  const gateActive =
    !clientConfig.loading && (currentSandboxMode === 'sandbox' || onDedicatedAdminHost);
  const bootstrapping = clientConfig.loading || siteAccessLoading || userLoading;
  const authenticated = Boolean(user);
  const needsPassCheck = gateActive && !bootstrapping && !authenticated;
  const frontDoorUrl = normalizedOrigin(clientConfig?.sandbox?.frontDoorUrl) || null;
  const shouldBounce = needsPassCheck && verification !== null && verification.valid !== true;

  useEffect(() => {
    if (!needsPassCheck) return undefined;
    let alive = true;
    const pass = readPassFromLocation();
    if (!pass) {
      setVerification({ valid: false, identity: null });
      return undefined;
    }
    verifySandboxPass(api, pass).then((result) => {
      if (alive) setVerification(result);
    });
    return () => {
      alive = false;
    };
  }, [needsPassCheck]);

  useEffect(() => {
    if (!shouldBounce) return;
    if (!frontDoorUrl) {
      console.warn(
        '[sandbox-entry] No front-door URL is configured for this gated host; staying on the sign-in page instead of bouncing.',
      );
      return;
    }
    window.location.assign(frontDoorUrl);
  }, [shouldBounce, frontDoorUrl]);

  // NEVER bounce (or leak gated content) while anything is still loading.
  if (clientConfig.loading) return <div className="empty-state">Loading…</div>;
  if (!gateActive) return children;
  if (bootstrapping) return <div className="empty-state">Loading…</div>;
  if (authenticated) return children;
  if (verification === null) return <div className="empty-state">Loading…</div>;
  if (verification.valid === true) {
    return (
      <SandboxPassContext.Provider value={{ identity: verification.identity }}>
        {children}
      </SandboxPassContext.Provider>
    );
  }
  // Invalid/absent pass. If the front door is configured we are navigating
  // away (effect above); render the loading affordance in the meantime. If it
  // is not configured, fail OPEN to the login page rather than looping or
  // rendering a blank screen.
  if (!frontDoorUrl) return children;
  return <div className="empty-state">Loading…</div>;
}

function AppRoutes() {
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/login" element={<PersonaLoginPage />} />
      <Route path="/request-access" element={<PublicRequestAccessPage />} />
      <Route
        path="/change-password"
        element={
          <RequireRoute roles={[]} allowMustChangePassword>
            <ChangePasswordPage />
          </RequireRoute>
        }
      />
      <Route path="/site-access" element={<SiteAccessRouteRedirect />} />
      <Route path="/status/:shareId" element={<PublicStatusPage />} />

      <Route
        element={
          <RequireRoute roles={[]}>
            <AppShell />
          </RequireRoute>
        }
      >
        <Route path="/home" element={guardedElement('/home', <NexusHomePage />)} />
        <Route
          path="/executive"
          element={guardedElement('/executive', <ExecutiveCommandCenterPage />)}
        />
        <Route path="/portfolios" element={guardedElement('/portfolios', <PortfolioListPage />)} />
        <Route
          path="/portfolios/:id"
          element={guardedElement('/portfolios', <PortfolioProfilePage />)}
        />
        <Route path="/programs" element={guardedElement('/programs', <ProgramListPage />)} />
        <Route path="/programs/:id" element={guardedElement('/programs', <ProgramProfilePage />)} />
        <Route path="/projects" element={guardedElement('/projects', <ProjectListPage />)} />
        <Route path="/projects/:id" element={guardedElement('/projects', <ProjectProfilePage />)} />
        <Route
          path="/project-factory"
          element={guardedElement('/project-factory', <ProjectFactoryPage />)}
        />
        <Route
          path="/project-factory/requests/new"
          element={guardedElement('/project-factory', <ProjectFactoryCreatePage />)}
        />
        <Route
          path="/project-factory/requests/:id"
          element={guardedElement('/project-factory', <ProjectFactoryRequestPage />)}
        />
        <Route
          path="/loe-dashboard"
          element={guardedElement('/loe-dashboard', <LoeDashboardPage />)}
        />
        <Route
          path="/analytics/loes/:id"
          element={guardedElement('/analytics/loes', <LoeDetailPage />)}
        />
        <Route path="/actions" element={guardedElement('/actions', <ActionCenterPage />)} />
        <Route path="/actions/:id" element={guardedElement('/actions', <ActionDetailPage />)} />
        <Route path="/meetings" element={guardedElement('/meetings', <MeetingManagerPage />)} />
        <Route path="/meetings/:id" element={guardedElement('/meetings', <MeetingDetailPage />)} />
        <Route path="/knowledge" element={guardedElement('/knowledge', <KnowledgeLibraryPage />)} />
        <Route
          path="/resources"
          element={guardedElement('/resources', <ResourceCommandCenterPage />)}
        />
        <Route
          path="/resources/skills"
          element={guardedElement('/resources/skills', <ResourceSkillsMatrixPage />)}
        />
        <Route
          path="/resources/allocations/:id"
          element={guardedElement('/resources/allocations/:id', <ResourceConflictDetailPage />)}
        />
        <Route
          path="/resources/assignments/new"
          element={guardedElement('/resources/assignments/new', <AssignmentWizardPage />)}
        />
        <Route path="/timesheets" element={guardedElement('/timesheets', <TimesheetHomePage />)} />
        <Route
          path="/timesheets/current"
          element={guardedElement('/timesheets/current', <CurrentTimesheetPage />)}
        />
        <Route
          path="/timesheets/:id"
          element={guardedElement('/timesheets', <TimesheetDetailPage />)}
        />
        <Route path="/governance" element={guardedElement('/governance', <GovernanceHomePage />)} />
        <Route
          path="/governance/risks"
          element={guardedElement('/governance/risks', <RiskRegisterPage />)}
        />
        <Route
          path="/governance/issues"
          element={guardedElement('/governance/issues', <IssueLogPage />)}
        />
        <Route
          path="/governance/changes"
          element={guardedElement('/governance/changes', <ChangeRequestsPage />)}
        />
        <Route
          path="/governance/evm"
          element={guardedElement('/governance/evm', <EvmDashboardPage />)}
        />
        <Route
          path="/knowledge/lessons-learned"
          element={guardedElement('/knowledge', <LessonsLearnedLibraryPage />)}
        />
        <Route
          path="/knowledge/artifacts/:id"
          element={guardedElement('/knowledge', <ArtifactDetailPage />)}
        />
        <Route
          path="/projects/:id/closeout"
          element={guardedElement('/knowledge', <CloseoutPackagePage />)}
        />
        <Route path="/rapids" element={guardedElement('/rapids', <RapidCommandCenterPage />)} />
        <Route path="/rapids/:id" element={guardedElement('/rapids', <RapidDetailPage />)} />
        <Route
          path="/projects/:projectId/gate-review/:gateId"
          element={guardedElement('/projects', <ProjectGateReviewPage />)}
        />

        <Route
          path="/delivery"
          element={guardedElement('/delivery', <IntegratedDeliveryWorkspacePage />)}
        />
        <Route
          path="/work-items/:id"
          element={guardedElement('/work-items/:id', <WorkItemDetailPage />)}
        />
        <Route path="/roadmap" element={guardedElement('/roadmap', <StrategicRoadmapPage />)} />
        <Route
          path="/financials"
          element={guardedElement('/financials', <FinancialLifecycleManagerPage />)}
        />
        <Route
          path="/financials/adjustments"
          element={guardedElement('/financials/adjustments', <FinancialAdjustmentsPage />)}
        />
        <Route
          path="/financials/funding-sources"
          element={guardedElement('/financials/funding-sources', <FundingSourcesPage />)}
        />
        <Route
          path="/lifecycle"
          element={guardedElement('/lifecycle', <PortfolioLifecycleManagementPage />)}
        />
        <Route
          path="/decision-support"
          element={guardedElement('/decision-support', <StrategicDecisionWorkspacePage />)}
        />
        <Route
          path="/market-research"
          element={guardedElement('/market-research', <MarketResearchTrackerPage />)}
        />

        <Route
          path="/ai/orchestration"
          element={guardedElement('/ai/orchestration', <AiOrchestrationHubPage />)}
        />
        <Route
          path="/ai/runs/:id"
          element={guardedElement('/ai/orchestration', <AiRunDetailPage />)}
        />
        <Route path="/ai/ask" element={guardedElement('/ai/ask', <AiAskPage />)} />

        <Route
          path="/admin/data"
          element={
            <RequireRoute roles={[rbac.ROLE_IDS.DATA_STEWARD, rbac.ROLE_IDS.SYSTEM_ADMIN]}>
              <DataIntegrationRbacSettingsPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/custom-fields"
          element={
            <RequireRoute roles={[rbac.ROLE_IDS.DATA_STEWARD, rbac.ROLE_IDS.SYSTEM_ADMIN]}>
              <AdminConfigurationPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/forms"
          element={
            <RequireRoute roles={[rbac.ROLE_IDS.DATA_STEWARD, rbac.ROLE_IDS.SYSTEM_ADMIN]}>
              <AdminConfigurationPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/rubrics"
          element={
            <RequireRoute roles={[rbac.ROLE_IDS.DATA_STEWARD, rbac.ROLE_IDS.SYSTEM_ADMIN]}>
              <AdminConfigurationPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/config/report-registry"
          element={
            <RequireRoute roles={[rbac.ROLE_IDS.DATA_STEWARD, rbac.ROLE_IDS.SYSTEM_ADMIN]}>
              <AdminConfigurationPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/ingestion"
          element={
            <RequireRoute roles={[rbac.ROLE_IDS.DATA_STEWARD, rbac.ROLE_IDS.SYSTEM_ADMIN]}>
              <IngestionCenterPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/ingestion/:jobId"
          element={
            <RequireRoute roles={[rbac.ROLE_IDS.DATA_STEWARD, rbac.ROLE_IDS.SYSTEM_ADMIN]}>
              <IngestionJobDetailPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/data/connectors/:id"
          element={
            <RequireRoute roles={[rbac.ROLE_IDS.DATA_STEWARD, rbac.ROLE_IDS.SYSTEM_ADMIN]}>
              <ConnectorDetailPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/lineage"
          element={
            <RequireRoute roles={[rbac.ROLE_IDS.DATA_STEWARD, rbac.ROLE_IDS.SYSTEM_ADMIN]}>
              <LineageExplorerPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/rbac"
          element={
            <RequireRoute roles={[rbac.ROLE_IDS.SYSTEM_ADMIN]}>
              <RbacAdminPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/site-access"
          element={
            <RequireRoute roles={[rbac.ROLE_IDS.SYSTEM_ADMIN]}>
              <SiteAccessAdminPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/access-requests"
          element={
            <RequireRoute
              roles={[
                rbac.ROLE_IDS.SYSTEM_ADMIN,
                rbac.ROLE_IDS.SECURITY_COMPLIANCE_REVIEWER,
                rbac.ROLE_IDS.DATA_STEWARD,
                rbac.ROLE_IDS.PORTFOLIO_MANAGER,
              ]}
            >
              <AccessRequestQueuePage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/access-requests/:id"
          element={
            <RequireRoute
              roles={[
                rbac.ROLE_IDS.SYSTEM_ADMIN,
                rbac.ROLE_IDS.SECURITY_COMPLIANCE_REVIEWER,
                rbac.ROLE_IDS.DATA_STEWARD,
                rbac.ROLE_IDS.PORTFOLIO_MANAGER,
              ]}
            >
              <AccessReviewDetailPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/users"
          element={
            <RequireRoute roles={[rbac.ROLE_IDS.SYSTEM_ADMIN]}>
              <UserAdminPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/authentication"
          element={
            <RequireRoute roles={[rbac.ROLE_IDS.SYSTEM_ADMIN]}>
              <AuthenticationAdminPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/audit"
          element={
            <RequireRoute
              roles={[rbac.ROLE_IDS.SYSTEM_ADMIN, rbac.ROLE_IDS.SECURITY_COMPLIANCE_REVIEWER]}
            >
              <AuditLogPage />
            </RequireRoute>
          }
        />
        <Route
          path="/admin/guide-results"
          element={guardedElement('/admin/guide-results', <GuideResultsWorkspacePage />)}
        />
        <Route
          path="/admin/activity"
          element={guardedElement('/admin/activity', <ActivityReportsPage />)}
        />
        <Route
          path="/admin/activity/reports/:id"
          element={guardedElement('/admin/activity/reports/:id', <ActivityReportViewerPage />)}
        />
        <Route
          path="/admin/compliance"
          element={
            <RequireRoute
              roles={[rbac.ROLE_IDS.SYSTEM_ADMIN, rbac.ROLE_IDS.SECURITY_COMPLIANCE_REVIEWER]}
            >
              <ComplianceReadinessPage />
            </RequireRoute>
          }
        />

        <Route path="/access/request" element={<AccessRequestPage />} />
        <Route path="/access/request/:id" element={<AccessRequestPage />} />
        <Route path="/access/status" element={<AccessStatusPage />} />

        <Route path="/search" element={<SearchResultsPage />} />
        {/* Tester feedback surfaces are open to every authenticated persona
            (W1B regate); only the governed triage queue keeps its role gate. */}
        <Route path="/feedback" element={guardedElement('/feedback', <MyFeedbackPage />)} />
        <Route
          path="/feedback/queue"
          element={guardedElement('/feedback/queue', <FeedbackQueuePage />)}
        />
        <Route
          path="/feedback/workspace"
          element={guardedElement('/feedback', <FeedbackWorkspacePage />)}
        />
        <Route
          path="/feedback/submitted/:packageId"
          element={guardedElement('/feedback', <FeedbackSubmittedPage />)}
        />
        {/* Typed help/intake framework, commit 2. Declared before the
            '/feedback/:packageId' pattern for readability; React Router already
            ranks the literal 'support' segment above the dynamic one. */}
        <Route
          path="/feedback/support/new"
          element={guardedElement('/feedback/support/new', <SupportTicketPage />)}
        />
        <Route
          path="/feedback/support/:intakeId"
          element={guardedElement('/feedback/support/:intakeId', <SupportTicketPage />)}
        />
        {/* Commit 4 — same literal-before-dynamic reasoning as 'support' above. */}
        <Route
          path="/feedback/request/new"
          element={guardedElement('/feedback/request/new', <RequestIntakePage />)}
        />
        <Route
          path="/feedback/request/:intakeId"
          element={guardedElement('/feedback/request/:intakeId', <RequestIntakePage />)}
        />
        <Route
          path="/feedback/:packageId/edit"
          element={guardedElement('/feedback', <FeedbackWorkspacePage />)}
        />
        <Route
          path="/feedback/:packageId"
          element={guardedElement('/feedback', <FeedbackWorkspacePage />)}
        />
        <Route path="/uat" element={guardedElement('/uat', <UatDashboardPage />)} />
        <Route
          path="/notifications"
          element={guardedElement('/notifications', <NotificationsPage />)}
        />
        <Route path="/activity" element={guardedElement('/activity', <ActivityPage />)} />
        <Route path="/saved-views" element={guardedElement('/saved-views', <SavedViewsPage />)} />
        <Route
          path="/products"
          element={guardedElement('/products', <BriefingProductFactoryPage />)}
        />
        <Route
          path="/status-packs"
          element={guardedElement('/status-packs', <StatusPackLibraryPage />)}
        />
        <Route
          path="/status-packs/:id"
          element={guardedElement('/status-packs', <StatusPackDetailPage />)}
        />
        <Route
          path="/briefings/:id"
          element={guardedElement('/products', <BriefingDetailPage />)}
        />
        <Route path="/help" element={guardedLazy('/help', HelpCenterPage)} />
        <Route path="/help/glossary" element={guardedLazy('/help/glossary', GlossaryPage)} />
        <Route
          path="/help/capability-map"
          element={guardedLazy('/help/capability-map', CapabilityMapPage)}
        />
        {/* Typed help/intake framework, commit 5. Open to every authenticated
            persona, like the other typed intakes. */}
        <Route
          path="/help/questions"
          element={guardedElement('/help/questions', <HelpQuestionsPage />)}
        />
        <Route path="/me" element={guardedElement('/me', <MyProfilePage />)} />
        <Route path="/settings" element={guardedElement('/settings', <UserSettingsPage />)} />

        {/* Legacy alias redirects (02 §A register, AC-REDIR-1..5) — one
              guarded redirect per LEGACY_ROUTE_ALIASES row, rendered inside
              this shell layout route so each alias passes through the same
              guard chain as its canonical target. */}
        {renderLegacyRedirectRoutes()}

        <Route path="/not-authorized" element={<AccessDeniedPage />} />
        <Route path="*" element={<NotFoundPage />} />
      </Route>
    </Routes>
  );
}

function SiteAccessBoundary() {
  const { loading, state } = useSiteAccess();
  const clientConfig = useClientConfig();
  const location = useLocation();
  const sandboxFrontDoorEnabled =
    clientConfig?.sandbox?.frontDoorEnabled === true ||
    clientConfig?.flags?.sandboxFrontDoorEnabled === true;
  const adminOrigin = clientConfig?.sandbox?.adminOrigin || null;
  const onDedicatedAdminHost =
    typeof window !== 'undefined' &&
    adminOrigin &&
    String(window.location.origin || '').replace(/\/+$/, '') ===
      String(adminOrigin).replace(/\/+$/, '');
  const allowPublicFrontDoorPath =
    sandboxFrontDoorEnabled === true &&
    !onDedicatedAdminHost &&
    ['/', '/login', '/request-access', '/site-access'].includes(location.pathname);
  const allowLocalAdminLogin =
    location.pathname === '/login' &&
    state?.adminConsoleEnabled === true &&
    !state?.edipn &&
    !state?.automationBypass;
  const allowPasswordFirstLogin =
    location.pathname === '/login' &&
    (clientConfig.loading || passwordCapableLoginSurface(clientConfig));
  // WP1: private sandbox instances are password-only surfaces guarded by the
  // routing-pass gate (SandboxEntryGuard). The CAC-bound site-access portal is
  // the wrong surface there — deep links and bare `/` fall through to the
  // guard, which bounces pass-less signed-out visitors to the front door.
  const sandboxInstancePassGate = sandboxMode(clientConfig) === 'sandbox';
  // While the client config is still loading on a blocked request we cannot
  // yet tell a sandbox-instance host from a CAC-gated one — hold the loading
  // affordance instead of flashing the portal.
  const waitForClientConfigWhileBlocked = clientConfig.loading && isSiteAccessBlocked(state);

  if (loading || waitForClientConfigWhileBlocked) {
    return <div className="empty-state">Loading…</div>;
  }

  if (
    isSiteAccessBlocked(state) &&
    !sandboxInstancePassGate &&
    !allowLocalAdminLogin &&
    !allowPasswordFirstLogin &&
    !allowPublicFrontDoorPath
  ) {
    if (location.pathname !== '/site-access') {
      return (
        <Navigate
          to="/site-access"
          replace
          state={{ fromStatusShare: STATUS_SHARE_ROUTE_PATTERN.test(location.pathname) }}
        />
      );
    }
    return <SiteAccessPortalPage />;
  }

  return (
    <CurrentUserProvider>
      <SandboxEntryGuard>
        <AppRoutes />
      </SandboxEntryGuard>
    </CurrentUserProvider>
  );
}

export function App() {
  return (
    <SiteAccessProvider>
      <SiteAccessBoundary />
    </SiteAccessProvider>
  );
}
